//Contracts:
import { particles } from 'app/contracts/Particles';
//Enums:
import { LookupType, RepellType, HomeParticleStateType } from 'app/enum/Particles';
//Library:
import { Particle } from 'particles/Particle';
import { ParticleProgressParallax } from 'particles/behaviour/ParticleProgressParallax';
import { ParticleMouseParallax } from 'particles/behaviour/ParticleMouseParallax';
import { ParticleRepell } from 'particles/behaviour/ParticleRepell';
import { ParticleAttract } from 'particles/behaviour/ParticleAttract';
import { ParticlePath } from 'particles/behaviour/ParticlePath';
import { ParticleTrailEmitter } from 'particles/behaviour/ParticleTrailEmitter';
import { ParticleTrail } from 'particles/behaviour/ParticleTrail';
import { ParticleCounter } from 'particles/behaviour/ParticleCounter';

//Controllers:
import { HomeField } from 'app/views/home/webgl/particles/fields/HomeField';
//(an auto-imported duplicate `import { particles } from 'pixi.js'` used to sit here —
//pixi.js has no such export and the contracts import above is the one in use)
import { ParticleHover } from 'particles/behaviour/ParticleHover';
import DelayHelper from 'utils/timers';

export class ScreensGridParticle extends Particle {
	private _field: HomeField;
	private _activeState: HomeParticleStateType;
	private _repell: ParticleRepell;
	private _attract: ParticleAttract;
	private _orbitPath: ParticlePath;
	private _iconPath: ParticlePath;
	private _iconPath2: ParticlePath;
	private _triPath: ParticlePath;
	private _rectPath: ParticlePath;
	private _mouseParallax: ParticleMouseParallax;
	private _trailEmitter: ParticleTrailEmitter;
	private _trail: ParticleTrail;
	private _hover: ParticleHover;
	private _counter: ParticleCounter;

	private _openingHoverParticle: boolean;


	private _homeIconSide: number;

	private _homeIntroComplete: boolean = false;
	private _mouseDownXEase: number = 1;


	public set field(v: HomeField) {
		this._field = v;
	}

	public get field(): HomeField {
		return this._field;
	}

	public get isHomeTrail(): boolean {
		return this._randoms.homeSpare === true;
	}

	public changeState(state: HomeParticleStateType, brute: boolean = false): void {
		this._activeState = state;


		this.deactivateAllBehaviours(true);

		if (state === HomeParticleStateType.OPENING) {
			this._openingState();
		} else if (state === HomeParticleStateType.OPENING_HOVER) {
			this._openingHoverState();
		} else if (state === HomeParticleStateType.HOME) {
			this._iconHomeState();
		} else if (state === HomeParticleStateType.BLACK_HOLE_FOLLOW) {
			this._blackHoleFollow();
		} else if (state === HomeParticleStateType.HOME_SCREENS) {
			this._homeScreensState();
		} else if (state === HomeParticleStateType.HOME_SPACES) {
			this._homeSpacesState();
		//(An `else if (state === HomeParticleStateType.SCREENS)` branch used to
		//sit here, but no SCREENS member exists in the enum — at runtime the
		//comparison read undefined and could never match — and its handler
		//_screensState() was an empty stub anyway. Removed with the stub.)
		} else if (state === HomeParticleStateType.SPACES) {
			this._spacesState();
		}
	}

	public setMouseParallaxByScale(): void {
		this._animatedProps.setEase({
			x: this._animatedProps.targetScale * 100,
			y: this._animatedProps.targetScale * 50,
			scale: this._animatedProps.targetScale * 50
		});

		this._mouseParallax.setup({
			ease: { x: 20, y: 20 },
			power: { x: 0.05 * this._animatedProps.targetScale, y: 0.08 * this._animatedProps.targetScale }
		});
	}

	public tick(): void {
		super.tick();

		if (this._activeState === HomeParticleStateType.HOME) {
			this._homeTick();
		}

		//Orbit path can be scaled when mouse down:
		if (this._activeState === HomeParticleStateType.BLACK_HOLE_FOLLOW) {
			this._orbitPath.expandMuliplier = this._sceneProps.mouse.isDown
				? 1.75 + Math.abs(this._sceneProps.progress.velocity) * 30
				: 1;
			this._orbitPath.speedMuiliplier = this._sceneProps.mouse.isDown
				? 1.4 + Math.abs(this._sceneProps.progress.velocity) * 50
				: 1;

			this._repell.active = Math.abs(this._sceneProps.progress.velocity) > 0.001 ? false : true;

			this._position.xOffset = -1 * (this._sceneProps.progress.velocity * 1.5);

		}
	}

	public colourise(side: number): void {
		if (this._openingHoverParticle) {
			this._colouriseSide(side);
		}
	}

	public setIntialState(state: HomeParticleStateType): void {
		if (state === HomeParticleStateType.OPENING) {
			this._animatedProps.targetScale = this._animatedProps.scale = 0;
		}
	}

	public activateAsTrail(
		emitterParticleProps: particles.ParticleProps,
		emitterAnimatedProps: particles.ParticleProperties,
		lifetimeFramesBase: number,
		lifetimeFramesSeed: number
	): void {
		this.deactivateAllBehaviours(true);

		//Match the particle positiion to the

		this.position.active = false;
		this._animatedProps.x = this._animatedProps.targetX = emitterAnimatedProps.x;
		this._animatedProps.y = this._animatedProps.targetY = emitterAnimatedProps.y;

		//Match the colour of the emitter:
		this.colour.set(emitterParticleProps.ref.colour.get());

		//Now configure the trail item
		this._trail.active = true;
		this._trail.alpha = emitterAnimatedProps.alpha;
		this._trail.scale = emitterAnimatedProps.scale;
		this._trail.randomiseLifetime(lifetimeFramesBase, lifetimeFramesSeed);

		this._repell.active = true;
		this._repell.target = this._sceneProps.targets.mouse;
		this._repell.type = RepellType.PUSH;
		//Allow for 150px circle and very tight ring:
		this._repell.radius = 25;
		this._repell.randomisePower(10, 20);
	}

	//OVERRIDED FROM BASE:
	//-------------------------------------------------------------------------------------------------------

	protected createBehaviours(config: particles.ParticleConfig): void {
		//Only a small range of particles need to be attracted to the intro

		this._randoms = {};
		this._openingHoverParticle = Math.random() <= 0.25;
		this._openingHoverParticle = true;
		if (this._openingHoverParticle) {
			let coloured = Math.random() <= 0.4;

			if (coloured) {
				Math.random();
			}
		}

		//NOTE: getHomeIconSide is not declared on ParticleField/HomeField anywhere —
		//pre-existing situation preserved from the untyped window global
		//(sceneProps.homeField is the ex-globals.homeField registration):
		this._homeIconSide = (this._sceneProps.homeField as any).getHomeIconSide();

		if (this._homeIconSide) {
			this._randoms.iconParticle = true;
			this._colouriseSide(this._homeIconSide);
		} else {
			this._randoms.includeInHome = Math.random() <= 0.6;
		}

		//If particle not used in home for main side particles or emitter then can used for other usage (Like emitted particles)
		if (!this._randoms.includeInHome && !this._randoms.iconParticle) {
			this._randoms.homeSpare = true;
		}

		//Add the parallax position controller:
		this._position = this._behaviourManager.add(ParticleProgressParallax);

		//Mouse parallax on particles:
		this._mouseParallax = this._behaviourManager.add(ParticleMouseParallax);

		//Add orbit effect for blank hole and random ossocliation:
		this._orbitPath = this._behaviourManager.add(ParticlePath);
		this._orbitPath.path = LookupType.CIRCLE;
		this._orbitPath.randomise();

		this._iconPath = this._behaviourManager.add(ParticlePath);
		this._iconPath.path = LookupType.ICON;
		this._iconPath.randomise();

		this._iconPath2 = this._behaviourManager.add(ParticlePath);
		this._iconPath2.path = LookupType.ICON2;
		this._iconPath2.randomise();

		this._triPath = this._behaviourManager.add(ParticlePath);
		this._triPath.path = LookupType.TRI;
		this._triPath.randomise();

		this._rectPath = this._behaviourManager.add(ParticlePath);
		this._rectPath.path = LookupType.RECT;
		this._rectPath.randomise();

		//Add repell behaviour:
		this._attract = this._behaviourManager.add(ParticleAttract);
		this._repell = this._behaviourManager.add(ParticleRepell);

		this._mouseParallax.active = true;
		this._mouseParallax.setup({ ease: { x: 20, y: 20 }, power: { x: 0.05, y: 0.05 } });

		this._counter = this._behaviourManager.add(ParticleCounter);

		//If the particle can emit particles in a trail then
		this._trailEmitter = this._behaviourManager.add(ParticleTrailEmitter);

		//If the particle is spare then can used for trail.
		if (this._randoms.homeSpare) {
			this._trail = this._behaviourManager.add(ParticleTrail);
		}

		this._hover = this._behaviourManager.add(ParticleHover);

		//Deactivate all behaviours but the 'base' ones
		this.deactivateAllBehaviours(true);
	}

	//PRIVATE METHODS
	//--------------------------------------------------------------------------------------------------
	private _openingState(): void {
		this._animatedProps.targetScale = 0;
		this._animatedProps.setEase({
			x: 1,
			y: 1,
			scale: 1
		});
	}

	private _openingHoverState(): void {
		//If Particle has been selected for the range that get attracted to around the circle circle:
		//Otherwise just leave hidden on the grid:
		if (this._openingHoverParticle) {
			//Deactivate normal parallax positioning:

			//Attract to center:
			this._attract.active = true;
			this._attract.target = this._sceneProps.targets.mouse;
			this._attract.instant = true;

			this._repell.target = this._sceneProps.targets.screenCenter;

			//Repell from the center of the screen around the edge bounday of a radius:
			this._repell.active = true;
			this._repell.target = this._sceneProps.targets.screenCenter;
			this._repell.type = RepellType.BOUNDARY;
			this._repell.testAtCount = 1;
			//Allow for 150px circle and very tight ring:
			this._repell.radius = 80;
			this._repell.randomisePower(55, 25);

			this._animatedProps.setEase({ x: 4, y: 4 });

			//Activate the orbit path to create black hole effect around the intial hover element:
			//The orbit path also makes the scale of the particle

			this._animatedProps.targetAlpha = 0.1 + Math.random() * 0.3;

			this._setupPath(this._orbitPath, {
				x: [25, 5],
				y: [25, 5],
				speed: [0.5, 0.7],
				scale: [0.05, 0.15],
				alpha: [0.03, 0.2],
				randomise: { unsync: true }
			});

		}
	}

	private _blackHoleFollow(): void {
		if (this._sceneProps.mouse.isDown || this._sceneProps.progress.ratio != 0.5) {
			return;
		}

		//Repell from the center of the screen around the edge bounday of a radius:
		this._attract.active = false;
		this._attract.target = this._sceneProps.targets.mouse;
		this._attract.instant = true;
		this._attract.powerX = 0.001;
		this._attract.powerY = 0.001;

		this._repell.active = true;
		this._repell.target = this._sceneProps.targets.mouse;
		this._repell.type = RepellType.PUSH;
		this._repell.testAtCount = 1;
		//Allow for 150px circle and very tight ring:
		this._repell.radius = 120;
		this._repell.randomisePower(80, 40);

		this._position.trackMouse = true;

		//Activate the orbit path to create black hole effect around the intial hover element:
		//The orbit path also makes the scale of the particle
		this._animatedProps.setEase({
			x: 5 + Math.random() * 40,
			y: 3 + Math.random() * 40,
			scale: 10
		});

		this._animatedProps.targetAlpha = 0.3 + Math.random() * 0.4;

		this._setupPath(this._orbitPath, {
			x: [25, 60],
			y: [25, 60],
			speed: [0.5, 0.7],
			scale: [0.05, 0.15],
			alpha: [0.6, 0.2],
			randomise: { unsync: true }
		});
	}


	private _homeTick(): void {
		if (this._sceneProps.mouse.isDown && this._sceneProps.progress.ratio != 0.5) {
			this._animatedProps.setEase({
				x: this._mouseDownXEase
			});
		}

		if (this._homeIntroComplete) {
		}

		if (this._homeIconSide) {
		}
	}



	private _iconHomeState(): void {
		this._position.trackMouse = false;
		this._mouseParallax.active = false;

		this.colourise(1);

		if (this.hasTag('icon')) {
			this._repell.active = false;


			this._mouseParallax.active = false;

			this._animatedProps.targetScale = 0.12;

			let zIndexRatio = this._animatedProps.targetScale / 0.12;

			this._animatedProps.setEase({
				x: 1 + zIndexRatio * 19,
				y: 1 + zIndexRatio * 9,
				scale: 20
			});

			this._mouseDownXEase = 2 + Math.random() * 20;

			this._animatedProps.targetAlpha = 0.5;

			//Allow for 150px circle and very tight ring:
			this._repell.randomisePower(30, 60);

			this._orbitPath.active = false;
			this._attract.active = false;

			this._repell.active = true;

			this._position.trackMouse = false;

			this._repell.active = true;
			this._repell.target = this._sceneProps.targets.mouse;
			this._repell.type = RepellType.PUSH;
			this._repell.testAtCount = 1;
			//Allow for 150px circle and very tight ring:
			this._repell.radius = 50;
			this._repell.randomisePower(50, 10);

			//Lamp / Hover effect:
			this._hover.active = true;
			this._hover.radius = 200;
			this._hover.setAlpha(0.5, 1);
			this._hover.setScale(0.12, 0.16);
			this._hover.target = this._sceneProps.targets.mouse;

			return;
		}

		//Is a 'spare' particle. So in the case just hide quickly:
		this._animatedProps.targetScale = 0;
		this._animatedProps.targetAlpha = 0;
	}


	private _homeIconSpawn(): void {
		return;
		let type = 'no-trail';

		this._iconPath2.active = false;
		this._iconPath.active = false;
		let iconPath = Math.random() < 0.5 ? this._iconPath2 : this._iconPath;
		iconPath.active = true;

		this._setupPath(iconPath, {
			x: [147 * this._sceneProps.sizes.displayScale, 0],
			y: [235 * this._sceneProps.sizes.displayScale, 0],
			speed: [1.2, 2],
			randomise: { unsync: true }
		});

		//Move to correct target
		//TODO change this so the target is anchored to the D icon
		this.position.set({ x: 0.4879, y: 0.3376 }, true);

		this._attract.active = true;
		this._attract.instant = false;
		this._attract.targetPos = { x: 0.4879, y: 0.3376 };
		this._attract.powerX = 0.001;
		this._attract.powerY = 0.001;

		this._repell.active = true;
		this._repell.testAtCount = 2;
		this._repell.target = this._sceneProps.targets.mouse;
		this._repell.type = RepellType.POSITION;
		//Allow for 150px circle and very tight ring:
		this._repell.radius = 25;
		this._repell.randomisePower(0.01, 0.01);

		this._colouriseSide(this._homeIconSide);

		let particleIndex = this._homeIconSide === -1 ? 140 : 510;

		if (this._iconPath.active) {
			this._iconPath.pathIndex = particleIndex;
			this._iconPath.hardPositionUpdate();
			this._iconPath2.active = false;
		} else {
			this._iconPath2.pathIndex = particleIndex;
			this._iconPath2.hardPositionUpdate();
		}

		if (type === 'trail') {
			this._trailEmitter.active = true;
			this._trailEmitter.randomiseChildLifetime(120, 40);
			this._trailEmitter.randomiseSpawnFrames(1, 0);
		} else {
			this._trailEmitter.active = false;
		}

		let speedScreens = type === 'trail' ? 3.5 : 2;
		let speedSpaces;
		if (this._iconPath2.active) {
			speedSpaces = type === 'trail' ? 2 : 1;
		} else {
			speedSpaces = type === 'trail' ? 3 : 1.6;
		}

		if (this._homeIconSide === -1) {
			this._counter.randomiseTriggerAt(130, 60);
			this._iconPath.speed = speedScreens * 0.9;
			this._iconPath2.speed = speedScreens * 0.8;
		} else {
			this._counter.randomiseTriggerAt(210, 60);
			this._iconPath.speed = speedSpaces * 0.8;
			this._iconPath2.speed = speedSpaces * 0.8;
		}

		let lifetimeBase = type === 'trail' ? 230 : 100;
		let lifetimeSeed = type === 'trail' ? 60 : 30;
		this._trailEmitter.randomiseLifetime(lifetimeBase, lifetimeSeed);

		if (type === 'trail') {
			this._animatedProps.alpha = this._animatedProps.targetAlpha = 0.3;
			this._animatedProps.scale = this._animatedProps.targetScale = 0.25;
			this._iconPath2.direction = Math.random() < 0.5 ? -1 : 1;
			this._iconPath.direction = Math.random() < 0.5 ? -1 : 1;
		} else {
			this._animatedProps.alpha = this._animatedProps.targetAlpha = 0.2;
			this._animatedProps.scale = this._animatedProps.targetScale = 0.25;
			this._iconPath2.direction = this._sceneProps.mouse.cords.x < this._sceneProps.sizes.window.w / 2 ? -1 : 1;
			this._iconPath.direction = this._sceneProps.mouse.cords.x < this._sceneProps.sizes.window.w / 2 ? -1 : 1;
		}

		this._trailEmitter.alpha = this._animatedProps.alpha;
		this._trailEmitter.scale = this._animatedProps.scale;
		this._orbitPath.active = false;

		this._counter.onTrigger = () => {
			this._homeIconSpawn();
		};
		this._counter.start();
	}

	private _setupPath(target: ParticlePath, props: any): void {
		target.active = true;

		target.xMuliplier = props.x[0] + Math.random() * props.x[1];
		target.yMuliplier = props.y[0] + Math.random() * props.y[1];
		target.speed = props.speed[0] + Math.random() * props.speed[1];

		if (props.alpha) {
			target.alphaActive = true;
			target.alphaRange = { min: props.alpha[0], max: props.alpha[1] };
		} else {
			target.alphaActive = false;
		}

		if (props.scale) {
			target.scaleActive = true;
			target.scaleRange = { min: props.scale[0], max: props.scale[1] };
		} else {
			target.scaleActive = false;
		}

		target.direction = props.direction ? props.direction : 1;

		if (props.randomise) {
			this._orbitPath.randomise(props.randomise.unsync, props.randomise.direction);
		}
	}

	private _homeScreensState(): void {
		this._colouriseSide(1);

		if (this.hasTag('shape')) {
			this._repell.active = true;

			this._position.trackMouse = false;

			this._repell.active = true;
			this._repell.target = this._sceneProps.targets.mouse;
			this._repell.type = RepellType.PUSH;
			this._repell.testAtCount = 1;
			//Allow for 150px circle and very tight ring:
			this._repell.radius = 50;
			this._repell.randomisePower(50, 10);

			this._mouseParallax.active = true;
			this._mouseParallax.setPower({
				x: 0.001,
				y: 0.001
			});

			this._animatedProps.setEase({
				x: 10 + Math.random() * 50,
				y: 8 + Math.random() * 40,
				scale: 30,
				alpha: 30
			});

			this._animatedProps.targetAlpha = 0.7 + Math.random() * 0.3;

			this._orbitPath.active = false;
			this._attract.active = false;

			this._animatedProps.targetAlpha = 0.9 + Math.random() * 0.1;
			this._animatedProps.targetScale = 0.1 + Math.random() * 0.2;

			DelayHelper.setTimeout(() => {
				this._animatedProps.targetScale = 0.07 + Math.random() * 0.05;
				this._mouseParallax.active = true;
				this._mouseParallax.setPower({
					x: 0.015,
					y: 0.03
				});


				DelayHelper.setTimeout(() => {
					this._hover.active = true;
					this._hover.radius = 80;
					this._hover.setAlpha(this._animatedProps.targetAlpha, this._animatedProps.targetAlpha + 0.5);
					this._hover.setScale(this._animatedProps.targetScale, this._animatedProps.targetScale + 0.1);
					this._hover.target = this._sceneProps.targets.mouse;
				}, 500);

				this._repell.active = true;
			}, 1000);

			return;
		}


		this.position.set({ x: 0.165, y: 0.4 });
		this.position.trackMouse = false;

		this._setupPath(this._rectPath, {
			x: [350, 2],
			y: [400, 2],
			speed: [1, 1],
			scale: [0.05, 0.15],
			alpha: [0.03, 0.2],
			randomise: { unsync: true }
		});

		this.colourise(-2);
		//Is a 'spare' particle. So in the case just hide quickly:

		this._animatedProps.targetScale = 0.05 + Math.random() * 0.5;
		this._animatedProps.targetAlpha = 0.1 + Math.random() * 0.3;

		this._mouseParallax.active = true;
		this._mouseParallax.setPower({
			x: 0.005,
			y: 0.008
		});

		this._repell.active = true;
		this._repell.target = this._sceneProps.targets.mouse;
		this._repell.type = RepellType.PUSH;
		this._repell.testAtCount = 1;
		//Allow for 150px circle and very tight ring:
		this._repell.radius = 40;
		this._repell.randomisePower(40, 100);
	}

	public setFrame(config): void {
		this.position.xOffset = 0;
		this.position.set({ x: config.x, y: config.y });
		this._rectPath.xMuliplier = config.w + Math.random() * 2;
		this._rectPath.yMuliplier = config.h + Math.random() * 2;
	}

	private _homeSpacesState(): void {
		this._position.trackMouse = false;

		if (this.hasTag('grid')) {
			this.setMouseParallaxByScale();
			DelayHelper.setTimeout(() => this.setMouseParallaxByScale(), 1000);
			this._mouseParallax.active = true;
			this._tint.set(0xffffff);
			return;
		}

		this._animatedProps.targetScale = 0;
		this._animatedProps.targetAlpha = 0;
	}

	private _colouriseSide(side: number): void {
		if (side == -2) {
			this.colour.set(0xffffff);
		} else if (side === -1) {
			this.colour.set(
				this._sceneProps.colours.arrays.homeScreensSide[
					Math.floor(Math.random() * (this._sceneProps.colours.arrays.homeScreensSide.length - 0.01))
				]
			);
		} else {
			this.colour.set(
				this._sceneProps.colours.arrays.homeSpacesSide[
					Math.floor(Math.random() * (this._sceneProps.colours.arrays.homeScreensSide.length - 0.01))
				]
			);
		}
	}

	private _spacesState(): void {}
}
